import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import type { Invoice, InvoiceNew, InvoiceQuery } from "./invoice";
import { apiHosts } from "@/api/constants/apiHosts";
import { NotifyOptionsError, NotifyOptionsSuccess } from "@modernary/mui/components/notifications";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import invoiceService from "./invoiceService";
import type { AxiosResponse } from "axios";

// Globals
const URL = "invoices";
const ENTITY_NAME = "Invoice";

const axiosParams = {
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<Invoice>(apiHosts.AQMS, axiosParams);

// Functions
// GET
const getInvoices = (params: InvoiceQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

// GET
type InvoiceIds = {
  id?: string | number;
};
const getNewInvoice = async (ids: InvoiceIds) => {
  const defaultApp = {
    data: {
      ...invoiceService.getInitialData(),
      ...ids,
    },
  } as AxiosResponse<InvoiceNew, any>;

  return defaultApp;
};

// GET
const getInvoiceById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

// POST
const createInvoice = (invoice: Invoice, config: PagingApiAndNotifierConfig) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };
  return api.post(URL, invoice, {
    successNotifCb,
    errorNotifCb,
  });
};

// PUT
const updateInvoice = (invoice: Invoice, config: PagingApiAndNotifierConfig) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };
  return api.put(`${URL}/${invoice.id}`, invoice, {
    successNotifCb,
    errorNotifCb,
  });
};

// HELPER / EXTENDED
const saveInvoice = (invoice: Invoice, config = {}) => {
  if (invoice.id) {
    return updateInvoice(invoice, config);
  } else {
    return createInvoice(invoice, config);
  }
};

// DELETE
const deleteInvoiceById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };
  return api.delete(`${URL}/${id}`, {
    successNotifCb,
    errorNotifCb,
  });
};

export { getInvoices, getNewInvoice, getInvoiceById, createInvoice, updateInvoice, saveInvoice, deleteInvoiceById };
